/**
 * Client → Legal tab — every legal agreement on this client's record,
 * across ALL agencies (Iceberg, Image Traders, …). Read-focused view for
 * day-to-day reference: which version they signed, download the signed
 * copy, and jump to the template in the Legal Documents workspace.
 */
import React, { useCallback, useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Scale, Download, ExternalLink, Loader2, AlertTriangle, Building2,
} from "lucide-react";
import { toast } from "sonner";

const BACKEND = process.env.REACT_APP_BACKEND_URL || "";

const STATUS_STYLES = {
  pending: "bg-amber-50 text-amber-800 border-amber-200",
  signed: "bg-emerald-50 text-emerald-700 border-emerald-200",
  superseded: "bg-zinc-100 text-zinc-500 border-zinc-200",
  terminated: "bg-red-50 text-red-700 border-red-200",
};

export default function ClientLegalPage() {
  const { clientId } = useParams();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/legal-documents/agreements/by-client/${clientId}`);
      setRows(data || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load legal agreements");
    } finally { setLoading(false); }
  }, [clientId]);
  useEffect(() => { load(); }, [load]);

  const downloadSigned = async (a) => {
    try {
      const token = localStorage.getItem("token") || "";
      const res = await fetch(
        `${BACKEND}/api/legal-documents/agreements/${a.agreement_id}/signed-file`,
        { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const blob = await res.blob();
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.download = a.signed_file?.filename || `${a.document_title}_${a.version_string}_signed.pdf`;
      document.body.appendChild(link); link.click(); link.remove();
    } catch { toast.error("Download failed"); }
  };

  return (
    <div className="space-y-4" data-testid="client-legal-page">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h2 className="font-display font-bold text-xl flex items-center gap-2">
            <Scale className="h-5 w-5 text-[#002FA7]" /> Legal agreements
          </h2>
          <p className="text-xs text-zinc-500">
            Contracts on this client&apos;s record — the exact version signed, the
            stored signed copy, and whether the template has since moved on.
          </p>
        </div>
        <Button asChild size="sm" variant="outline" data-testid="client-legal-manage-link">
          <Link to="/legal-docs">
            Manage templates <ExternalLink className="h-3.5 w-3.5 ml-1.5" />
          </Link>
        </Button>
      </div>

      {loading ? (
        <Card className="rounded-sm"><CardContent className="p-6 flex items-center gap-2 text-sm text-zinc-500">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading…
        </CardContent></Card>
      ) : rows.length === 0 ? (
        <Card className="rounded-sm"><CardContent className="p-6 text-sm text-zinc-500">
          No legal agreements on record for this client yet. Assign one from
          the <Link to="/legal-docs" className="text-[#002FA7] underline">Legal Documents</Link> workspace.
        </CardContent></Card>
      ) : (
        <Card className="rounded-sm">
          <CardContent className="p-4 overflow-x-auto">
            <table className="min-w-full text-xs">
              <thead>
                <tr className="text-left text-[10px] uppercase tracking-widest text-zinc-500 border-b border-zinc-200">
                  <th className="py-1.5 pr-3">Document</th>
                  <th className="py-1.5 pr-3">Agency</th>
                  <th className="py-1.5 pr-3">Version</th>
                  <th className="py-1.5 pr-3">Status</th>
                  <th className="py-1.5 pr-3">Signed</th>
                  <th className="py-1.5 pr-3">Notes</th>
                  <th className="py-1.5" />
                </tr>
              </thead>
              <tbody>
                {rows.map((a) => (
                  <tr key={a.agreement_id} className="border-b border-zinc-100"
                    data-testid={`client-legal-row-${a.agreement_id}`}>
                    <td className="py-2 pr-3 font-medium">{a.document_title}</td>
                    <td className="py-2 pr-3 text-zinc-600">
                      <span className="inline-flex items-center gap-1">
                        <Building2 className="h-3 w-3 text-zinc-400" />
                        {a.document_company}
                      </span>
                    </td>
                    <td className="py-2 pr-3">
                      <span className="font-mono">{a.version_string}</span>
                      {a.is_outdated && (
                        <Badge className="ml-1.5 rounded-sm bg-amber-100 text-amber-800 border-transparent text-[9px] align-middle">
                          <AlertTriangle className="h-2.5 w-2.5 mr-0.5" />
                          template now {a.current_version}
                        </Badge>
                      )}
                    </td>
                    <td className="py-2 pr-3">
                      <Badge variant="outline"
                        className={`rounded-sm text-[10px] ${STATUS_STYLES[a.status] || ""}`}>
                        {a.status}
                      </Badge>
                    </td>
                    <td className="py-2 pr-3 text-zinc-600">
                      {a.signed_date || "—"}
                      {a.signed_by_name && <span className="text-zinc-400"> · {a.signed_by_name}</span>}
                    </td>
                    <td className="py-2 pr-3 text-zinc-500 max-w-[220px] truncate" title={a.notes}>
                      {a.notes || "—"}
                    </td>
                    <td className="py-2 text-right">
                      {a.signed_file?.path ? (
                        <Button size="sm" variant="outline" className="h-6 px-1.5 text-[10px] rounded-sm"
                          onClick={() => downloadSigned(a)}
                          data-testid={`client-legal-download-${a.agreement_id}`}>
                          <Download className="h-3 w-3 mr-1" /> Signed copy
                        </Button>
                      ) : (
                        <span className="text-[10px] text-zinc-400">no signed copy</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
